// inv/src/components/Footer.jsx
import { COMPANY_NAME, CONTACT_PHONE, CURRENT_YEAR, SOCIAL_X, SOCIAL_FACEBOOK, SOCIAL_LINKEDIN, SOCIAL_WHATSAPP } from '../config';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import logo from '../logo.svg';
import FacebookIcon from '../icons/facebook.svg';
import LinkedInIcon from '../icons/linkedin.svg';
import WhatsAppIcon from '../icons/whatsapp.svg';
import XIcon from '../icons/x.svg';
import logoFooter from '../crino_logo_1.svg';

const Footer = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const menuItems = [
    { path: '/', label: t('menu.home') },
    { path: '/find-investors/', label: t('menu.findInvestors') },
    { path: '/pricing/', label: t('menu.pricing') },
    { path: '/faq/', label: t('menu.faq') },
    { path: '/blog/', label: t('menu.blog') },
    { path: '/lists/', label: t('menu.lists') },
  ];

  const solutionsItems = [
    { path: '/for-early-stage-startups/', label: t('menu.solution1') },
    { path: '/for-established-businesses/', label: t('menu.solution2') },
    { path: '/for-accelerators-and-incubators/', label: t('menu.solution3') },
  ];

  const localizedHref = (path) =>
    i18n.language === 'en' ? path : `/${i18n.language}${path}`;

  return (
    <div className="bg-gradient-to-r from-slate-50 to-gray-100">
      <div className="container mx-auto px-20 py-12 sm:px-8">
        <footer>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 text-sm text-gray-600 w-full">
            {/* Sitemap */}
            <nav className="flex flex-col items-center sm:items-start">
              <h6 className="mb-4 text-md font-semibold text-stone-900">
                {t('footer.sitemap')}
              </h6>
              <ul className="text-center sm:text-left">
                {menuItems.map((menuItem) => (
                  <li key={menuItem.path} className="py-1">
                    <a
                      href={localizedHref(menuItem.path)}
                      className="hover:text-gray-900 transition duration-150 ease-in-out"
                    >
                      {menuItem.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Solutions */}
            <nav className="flex flex-col items-center sm:items-start">
              <h6 className="mb-4 text-md font-semibold text-stone-900">
                {t('menu.solutions')}
              </h6>
              <ul className="text-center sm:text-left">
                {solutionsItems.map((item) => (
                  <li key={item.path} className="py-1">
                    <a
                      href={localizedHref(item.path)}
                      className="hover:text-gray-900 transition duration-150 ease-in-out"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Legal info */}
            <nav className="flex flex-col items-center sm:items-start">
              <h6 className="mb-4 text-md font-semibold text-stone-900">
                {t('footer.legalInfo', 'Legal info')}
              </h6>
              <ul className="text-center sm:text-left">
                <li className="py-1">
                  <a
                    href={
                      i18n.language === 'en'
                        ? '/terms-and-conditions/'
                        : `/${i18n.language}/terms-and-conditions/`
                    }
                    className="hover:text-gray-900 transition duration-150 ease-in-out"
                  >
                    {t('footer.termsOfUse')}
                  </a>
                </li>
                <li className="py-1">
                  <a
                    href={
                      i18n.language === 'en'
                        ? '/privacy-policy/'
                        : `/${i18n.language}/privacy-policy/`
                    }
                    className="hover:text-gray-900 transition duration-150 ease-in-out"
                  >
                    {t('footer.privacyPolicy')}
                  </a>
                </li>
                <li className="py-1">
                  <a
                    href={
                      i18n.language === 'en'
                        ? '/pricing-policy/'
                        : `/${i18n.language}/pricing-policy/`
                    }
                    className="hover:text-gray-900 transition duration-150 ease-in-out"
                  >
                    {t('footer.pricingPolicy')}
                  </a>
                </li>
                <li className="py-1">
                  <a
                    href={
                      i18n.language === 'en'
                        ? '/refund-policy/'
                        : `/${i18n.language}/refund-policy/`
                    }
                    className="hover:text-gray-900 transition duration-150 ease-in-out"
                  >
                    {t('footer.refundPolicy')}
                  </a>
                </li>
                <li className="py-1">
                  <a
                    href={
                      i18n.language === 'en'
                        ? '/cookie-policy/'
                        : `/${i18n.language}/cookie-policy/`
                    }
                    className="hover:text-gray-900 transition duration-150 ease-in-out"
                  >
                    {t('footer.cookiePolicy')}
                  </a>
                </li>
              </ul>
            </nav>

            {/* Social + Contact */}
            <nav className="flex flex-col items-center justify-start sm:items-start">
              <h6 className="mb-4 text-md font-semibold text-stone-900">Social</h6>
              <div className="flex gap-4 justify-center mb-4">
                <a
                  href={SOCIAL_LINKEDIN}
                  target="_blank"
                  aria-label="LinkedIn"
                  className="hover:text-gray-900 transition duration-150 ease-in-out"
                  rel="noreferrer"
                >
                  <img src={LinkedInIcon} alt="LinkedIn" className="w-7 h-7" />
                </a>
                <a
                  href={SOCIAL_FACEBOOK}
                  target="_blank"
                  aria-label="Facebook"
                  className="hover:text-gray-900 transition duration-150 ease-in-out"
                  rel="noreferrer"
                >
                  <img src={FacebookIcon} alt="Facebook" className="w-7 h-7" />
                </a>
                <a
                  href={SOCIAL_X}
                  target="_blank"
                  aria-label="X"
                  className="hover:text-gray-900 transition duration-150 ease-in-out"
                  rel="noreferrer"
                >
                  <img src={XIcon} alt="X" className="w-7 h-7" />
                </a>
                <a
                  href={SOCIAL_WHATSAPP}
                  target="_blank"
                  aria-label="WhatsApp"
                  className="hover:text-gray-900 transition duration-150 ease-in-out"
                  rel="noreferrer"
                >
                  <img src={WhatsAppIcon} alt="WhatsApp" className="w-7 h-7" />
                </a>
              </div>

              <div className="text-center sm:text-left text-sm text-gray-600">
                <p className="py-1">
                  Email:{' '}
                  <a
                    href="mailto:contact@crino.io"
                    className="hover:text-gray-900"
                  >
                    contact@crino.io
                  </a>
                </p>
                <p className="py-1">
                  WhatsApp:{' '}
                  <a
                    href={SOCIAL_WHATSAPP}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-gray-900"
                  >
                    {CONTACT_PHONE}
                  </a>
                </p>
              </div>
            </nav>
          </div>

          <div className="mt-8 pt-6 flex flex-col justify-center items-center gap-4 text-center">
            <aside className="flex flex-col md:flex-row items-center gap-2">
              <img
                src={logoFooter}
                loading="lazy"
                alt="logo2"
                className="h-12 rounded-full ring-1 ring-stone-400 w-auto object-contain"
              />
              <p className="text-sm text-gray-600 mt-2 md:mt-0">
                {COMPANY_NAME} © {CURRENT_YEAR}
              </p>
            </aside>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Footer;
